/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { buttonSize, buttonVariant } from '../../registry/constants';
import { defaultValue, isValidButtonColor } from './Button';
import { analyzeButton } from './ButtonAnalyze';

const ButtonManager: ComponentManagerFn<'Button'> = (
  platform: Platform,
): ComponentManager<'Button'> => {
  return {
    ...baseComponentManager<'Button'>(platform, 'Button'),
    type: 'Button',
    name: 'Button',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'PulseTip'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['TriggerOnClick'],
    props: {
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        filterColor: isValidButtonColor,
      },
      fullWidth: {
        label: 'Full Width',
        type: 'boolean',
        helpText:
          'If checked, the button will take up the full width of its container.',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: buttonSize,
      },
      iconColor: {
        label: 'Icon Color',
        type: 'ColorExpression',
        helpText:
          'e.g. background.paper or background.default or primary.main or primary.dark or primary.light or any valid CSS color string',
      },
      startIcon: {
        label: 'Start Icon',
        type: 'iconselect',
        helpText: 'Icon to be placed before the text.',
        parentColorProp: 'color',
      },
      endIcon: {
        label: 'End Icon',
        type: 'iconselect',
        helpText: 'Icon to be placed after the text.',
        parentColorProp: 'color',
      },
      text: {
        label: 'Text',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      hotKey: {
        label: 'Hot Key',
        type: 'string',
        maxLength: 1,
        helpText: (item: RequiredItem<'Button'>) => {
          const key =
            item.props.hotKey ||
            (item.props.text.type === 'text' && item.props.text.value?.length
              ? item.props.text.value.charAt(0)
              : '<Hot Key>');

          return `A shortcut key to be mapped for this button click event. User can use Ctrl+Shift+${key} or Cmd+Shift+${key} as a keyboard shortcut instead of a mouse click on this button.`;
        },
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: buttonVariant,
      },
      disableElevation: {
        label: 'Disable Elevation',
        type: 'boolean',
      },
      onClickFunction: {
        label: 'On Click',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      ...toolTipProps,
    },
    isPropertyVisible(prop, itemData, { appUid, pageId, itemId }): boolean {
      const buttonGroupParent = platform.findParentOfType(
        appUid,
        pageId,
        itemId,
        'ButtonGroup',
      );
      switch (prop) {
        case 'color':
        case 'fullWidth':
        case 'size':
        case 'variant':
          return buttonGroupParent === null;
        default:
          return true;
      }
    },
    generateShortId: (item) =>
      item.text?.type === 'text' ? item.text.value : '',
    analyze: analyzeButton,
    helpRenders: [
      {
        title: 'Default Button',
        description: 'Button rendered with Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        item: {
          itemId: 'button',
          props: defaultValue,
          children: ['triggerOnClick'],
          type: 'Button',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Button Text & Icon',
        description:
          'Standard Button with Icon & Text properties specified. Try changing the properties shown on the right.',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        visibleProps: ['startIcon', 'text'],
        item: {
          itemId: 'button',
          props: {
            startIcon: {
              iconName: 'check',
              iconType: 'light',
            },
            text: {
              type: 'text',
              value: 'Submit',
            },
          },
          children: ['triggerOnClick'],
          type: 'Button',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Button Variants',
        description: 'Button rendered with different variant',
        page: SamplePage,
        clearParentChildren: true,
        exampleId: 'e3',
        visibleProps: ['variant'],
        item: {
          itemId: 'button',
          props: {
            startIcon: {
              iconName: 'check',
              iconType: 'light',
            },
            text: {
              type: 'text',
              value: 'Submit',
            },
            variant: 'text',
          },
          children: ['triggerOnClick'],
          type: 'Button',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Button Colors',
        description: 'Button rendered with different theme color',
        page: SamplePage,
        exampleId: 'e4',
        clearParentChildren: true,
        visibleProps: ['color'],
        item: {
          itemId: 'button',
          props: {
            startIcon: {
              iconName: 'trash',
              iconType: 'light',
            },
            text: {
              type: 'text',
              value: 'Delete',
            },
            color: 'error',
          },
          children: ['triggerOnClick'],
          type: 'Button',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Text Expression',
        description: 'Button text rendered dynamically using an expression',
        page: SamplePage,
        exampleId: 'e5',
        clearParentChildren: true,
        visibleProps: ['text'],
        item: {
          itemId: 'button',
          props: {
            startIcon: {
              iconName: 'check',
              iconType: 'light',
            },
            text: {
              type: 'expression',
              // eslint-disable-next-line no-template-curly-in-string
              value: '`Hi ${$.session.displayName}!`',
            },
          },
          children: ['triggerOnClick'],
          type: 'Button',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Tooltip',
        page: SamplePage,
        exampleId: 'e6',
        visibleProps: ['toolTip', 'toolTipPosition', 'toolTipMaxWidth'],
        clearParentChildren: true,
        height: 240,
        item: {
          itemId: 'button',
          props: {
            startIcon: {
              iconName: 'check',
              iconType: 'light',
            },
            text: {
              type: 'text',
              value: 'Submit',
            },
            toolTip: {
              type: 'text',
              value:
                'Buttons allow users to take actions, and make choices, with a single tap.',
            },
            toolTipPosition: 'top',
            toolTipMaxWidth: 200,
          },
          children: ['triggerOnClick'],
          type: 'Button',
          parent: 'columnPanel1',
        },
      },
    ],
    getDisplayText: (item) => item.props.text.value,
    replaceableWith: ['IconButton', 'ActionButton'],
    category: 'Toolbar',
    icon: { iconName: 'io-button', iconType: 'cloudio' },
    description:
      'Buttons allow users to take actions, and make choices, with a single tap.',
  };
};

export default ButtonManager;
